import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { g, p as post } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { UtensilsCrossed, Fish, Beef, Check, QrCode } from "lucide-react";
import { QrTavolo } from "@/components/QrTavolo";

const CAT = [
  { id: "primi", label: "Primi" },
  { id: "secondi", label: "Secondi" },
  { id: "contorni", label: "Contorni" },
];

/** Menu fisso del giorno: tre primi, tre secondi, tre contorni scelti da tendina. */
export default function MenuFisso() {
  const { tenant, modules } = useApp();
  const slug = tenant?.slug || "";
  const onFisso = !modules || modules.includes("menu_fisso");
  const onQr = !modules || modules.includes("carta_qr");
  const [liste, setListe] = useState(null);
  const [lista, setLista] = useState("terra");
  const [m, setM] = useState({ name: "Menu fisso", price_full: 15, price_light: 12, primi: ["", "", ""], secondi: ["", "", ""], contorni: ["", "", ""], active: true });
  const [salvato, setSalvato] = useState(false);

  useEffect(() => {
    g("/catalogo-piatti").then((d) => setListe(d.liste)).catch(() => setListe(null));
    g("/menu-fisso").then((d) => {
      const tre = (a) => [...(a || []), "", "", ""].slice(0, 3);
      setM({ name: d.name, price_full: d.price_full, price_light: d.price_light, active: d.active,
             primi: tre(d.primi), secondi: tre(d.secondi), contorni: tre(d.contorni) });
    }).catch(() => {});
  }, []);

  const opzioni = useMemo(() => (liste ? liste[lista] : null), [liste, lista]);
  const scelti = CAT.flatMap((c) => m[c.id].filter(Boolean));

  const set = (cat, i, val) => setM({ ...m, [cat]: m[cat].map((v, k) => (k === i ? val : v)) });

  const salva = async () => {
    try {
      await post("/menu-fisso", {
        name: m.name || "Menu fisso",
        price_full: Number(m.price_full) || 0,
        price_light: Number(m.price_light) || 0,
        primi: m.primi, secondi: m.secondi, contorni: m.contorni, active: m.active,
      });
      setSalvato(true);
      toast.success("Menu fisso salvato: lo trovi in comanda e i piatti sono nel menu");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Non è stato possibile salvare");
    }
  };

  return (
    <div className="space-y-6" data-testid="menu-fisso-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-amber-600">{onFisso ? "Pranzo di lavoro" : "Carta digitale"}</p>
        <h1 className="font-head mt-1 text-3xl font-bold sm:text-4xl">{onFisso ? "Menu fisso del giorno" : "Carta digitale con QR"}</h1>
        <p className="mt-2 max-w-2xl text-base text-slate-600">
          Scegli i piatti dalle tendine: entrano nel menu e in comanda compaiono due voci a prezzo fisso,
          completa e ridotta. Puoi anche scrivere un piatto che non è in elenco.
        </p>
      </div>

      <div className={`flex gap-2 ${onFisso ? "" : "hidden"}`} data-testid="menu-fisso-liste">
        {[["terra", "Carne e verdure", Beef], ["pesce", "Giornata di pesce", Fish]].map(([id, label, Icon]) => (
          <Button
            key={id}
            variant={lista === id ? "default" : "outline"}
            className="h-12"
            onClick={() => setLista(id)}
            data-testid={`lista-${id}`}
          >
            <Icon className="mr-2 h-4 w-4" /> {label}
          </Button>
        ))}
      </div>

      <div className={`grid gap-4 lg:grid-cols-3 ${onFisso ? "" : "hidden"}`}>
        {CAT.map((c) => (
          <Card key={c.id} className="space-y-3 p-5" data-testid={`blocco-${c.id}`}>
            <h2 className="font-head flex items-center gap-2 text-base font-bold md:text-lg">
              <UtensilsCrossed className="h-4 w-4 text-amber-500" /> {c.label}
            </h2>
            {[0, 1, 2].map((i) => (
              <div key={`${c.id}-${i}`} className="space-y-1.5">
                <select
                  className="h-12 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                  value={opzioni?.[c.id]?.some((o) => o.name === m[c.id][i]) ? m[c.id][i] : ""}
                  onChange={(e) => set(c.id, i, e.target.value)}
                  data-testid={`select-${c.id}-${i}`}
                >
                  <option value="">— scegli il {c.label.toLowerCase().slice(0, -1)} {i + 1} —</option>
                  {(opzioni?.[c.id] || []).map((o) => (
                    <option key={o.name} value={o.name}>{o.name} · {o.price.toFixed(2)} €</option>
                  ))}
                </select>
                <Input
                  className="h-11"
                  placeholder="…oppure scrivilo tu"
                  value={m[c.id][i]}
                  onChange={(e) => set(c.id, i, e.target.value)}
                  data-testid={`input-${c.id}-${i}`}
                />
              </div>
            ))}
          </Card>
        ))}
      </div>

      <Card className={`space-y-4 p-5 ${onFisso ? "" : "hidden"}`} data-testid="menu-fisso-prezzi">
        <h2 className="font-head text-base font-bold md:text-lg">Prezzi e pubblicazione</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <label className="space-y-1.5 text-sm">
            <span className="font-semibold text-slate-700">Nome del menu</span>
            <Input className="h-12" value={m.name} onChange={(e) => setM({ ...m, name: e.target.value })} data-testid="input-nome" />
          </label>
          <label className="space-y-1.5 text-sm">
            <span className="font-semibold text-slate-700">Completo (primo, secondo, contorno)</span>
            <Input className="h-12" type="number" step="0.5" value={m.price_full}
                   onChange={(e) => setM({ ...m, price_full: e.target.value })} data-testid="input-prezzo-completo" />
          </label>
          <label className="space-y-1.5 text-sm">
            <span className="font-semibold text-slate-700">Ridotto (un piatto e contorno)</span>
            <Input className="h-12" type="number" step="0.5" value={m.price_light}
                   onChange={(e) => setM({ ...m, price_light: e.target.value })} data-testid="input-prezzo-ridotto" />
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <input type="checkbox" className="h-4 w-4" checked={m.active}
                 onChange={(e) => setM({ ...m, active: e.target.checked })} data-testid="check-attivo" />
          In servizio oggi (se lo togli, in comanda resta ma segnato come non disponibile)
        </label>

        {scelti.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="menu-fisso-riepilogo">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-700">Oggi in tavola</p>
            <p className="mt-1 text-sm text-slate-700">{scelti.join(" · ")}</p>
          </div>
        )}

        <Button className="h-14 w-full bg-slate-900 text-base font-bold text-white hover:bg-slate-800 sm:w-auto sm:px-8"
                onClick={salva} data-testid="salva-menu-fisso">
          {salvato ? <Check className="mr-2 h-5 w-5" /> : null} Salva il menu del giorno
        </Button>
      </Card>
      <Card className={`space-y-3 p-5 ${onQr ? "" : "hidden"}`} data-testid="menu-fisso-qr">
        <h2 className="font-head text-base font-bold md:text-lg">QR da mettere sul tavolo</h2>
        <p className="text-sm text-slate-600">
          I clienti inquadrano e vedono la carta aggiornata: se cambi un prezzo, cambia anche sul loro
          telefono. Il foglio esce con due segnaposto da tagliare e piegare. Se scrivi il numero del
          tavolo, il cliente può chiamarti senza doverlo scegliere a mano.
        </p>
        <QrTavolo slug={slug} />
        <div className="flex flex-wrap gap-3">
          <a
            href={`/carta/${slug}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-12 items-center rounded-md border border-slate-200 px-6 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            data-testid="carta-link"
          >
            Guarda la carta come la vede il cliente
          </a>
        </div>
      </Card>
    </div>
  );
}
